//////////////////
// IMPORTS
//////////////////
// libraries
import * as React from "react";
import { IProps, ISlot, IRoom } from "./SuggestedTimesRow.types";
import { styles } from "./SuggestedTimesRow.styles";
import pluralize from "pluralize";
import { SchedulingAttendanceSummaryTag, Inconveniences } from "@clockwise/schema";
import { DateTime } from "luxon";
// components
import { AttendeesPains, AttendeesPainsCompact } from "@components/attendees-pains";
import { LogoHorizontal } from "@components/logo";
// constants
import { articleUrls } from "@clockwise/client-commons/src/constants/help-center";
// util
import {
  getAttendeesPainText,
  GetAttendeesPainTextPerson,
  GetAttendeesPainTextSchedulingSlot,
  IPerson,
} from "@clockwise/client-commons/src/util/scheduling-slot";
import * as SchedulingUtils from "@util/scheduling.util";
import { getRelativeFormat, CalendarSpec } from "@util/date.util";
// material ui
import {
  Flag,
  Waves,
  Star,
  TagFaces,
  MoodBad,
  Fastfood,
  ReportProblemOutlined,
  MeetingRoom,
  NoMeetingRoom,
  KeyboardArrowUp,
  KeyboardArrowDown,
} from "@material-ui/icons";
import { Button, Paper, Popper } from "@material-ui/core";
import { Skeleton } from "@material-ui/lab";
import { default as withStyles, CSSProperties } from "@material-ui/core/styles/withStyles";
import { Carousel } from "@ui/carousel";
import { HoverableChevron } from "@ui/hoverable-chevron";
import { motion } from "framer-motion";

//////////////////
// HELPERS
//////////////////

const getFormattedFocusTime = (focusTime: number) => {
  if (focusTime === 25 || focusTime === 30) {
    return "-30m";
  }

  if (focusTime < 25) {
    return "-15m";
  }

  // Over 10 hours, round up to nearest hour.
  if (focusTime > 600) {
    focusTime = Math.ceil(focusTime / 60) * 60;
  }

  const minutes = Math.ceil(focusTime / 30) * 30; // Turn speedy meeting duration into regular.
  return "-" + minutes / 60 + "h";
};

type CountImpactSlot = {
  attendanceImpacts: Array<{
    hasConflict: boolean;
    inconveniences: string[];
  }>;
};
export const countImpacts = (slot: CountImpactSlot) => {
  const impactTypes = new Set();
  let count = slot.attendanceImpacts?.filter((impact) => impact.hasConflict).length;

  if (count > 0) {
    impactTypes.add("Conflict");
  }

  count += slot.attendanceImpacts?.reduce((accum, impact) => {
    impact?.inconveniences?.forEach((incon: string) => impactTypes.add(incon));
    return accum + impact?.inconveniences?.length || 0;
  }, 0);

  return [count, impactTypes.size];
};

//////////////////////////////////
// Subcomponents used in the Tiles
//////////////////////////////////

export const getFormattedTag = (tags: SchedulingAttendanceSummaryTag[], index: number) => {
  const soloTag = (tags?.length && tags[0]) || "";
  let Icon,
    tagText = soloTag;

  if (
    tags?.includes(SchedulingAttendanceSummaryTag.Soonest) &&
    tags?.includes(SchedulingAttendanceSummaryTag.Best)
  ) {
    Icon = Star;
    tagText = "Soonest & best";
  } else {
    switch (soloTag) {
      case SchedulingAttendanceSummaryTag.Soonest:
        Icon = Flag;
        break;
      case SchedulingAttendanceSummaryTag.Best:
        Icon = Star;
        tagText = "Best pick";
        break;
      case SchedulingAttendanceSummaryTag.MostFocus:
        Icon = Waves;
        tagText = "Most focus";
        break;
      case SchedulingAttendanceSummaryTag.Inconvenient:
      case SchedulingAttendanceSummaryTag.Conflict:
        Icon = MoodBad;
        tagText = soloTag;
        break;
      case SchedulingAttendanceSummaryTag.OverLunch:
        Icon = Fastfood;
        tagText = "Over lunch";
        break;
      case SchedulingAttendanceSummaryTag.InFocusTime:
        Icon = Star;
        tagText = "In Focus Time";
        break;
      case SchedulingAttendanceSummaryTag.Good:
      default:
        // Shouldn't happen
        Icon = index === 1 ? Star : TagFaces;
        tagText = index <= 1 ? "Good pick" : "Also good";
    }
  }

  return {
    full: (
      <div>
        <Icon style={styles.tagIcon} />
        <span style={styles.tagText}>{tagText}</span>
      </div>
    ),
    icon: <Icon style={styles.tagIcon} />,
  };
};

interface NoSuggestionsProps {
  dateIsInPast: boolean;
  currentDurationString: string;
  onSetDate: (date: Date) => void;
  firstAvailableDate?: string;
}

export const NoSuggestions = ({
  dateIsInPast,
  firstAvailableDate,
  onSetDate,
  currentDurationString,
}: NoSuggestionsProps) => {
  let header = <span>No Clockwise suggestions available</span>,
    subheader = "Try broadening your search";
  if (dateIsInPast) {
    header = <span>Clockwise can't suggest times in the past</span>;
    subheader = "";
  }

  if (firstAvailableDate) {
    const date = SchedulingUtils.dateObjectFromGcalDate(firstAvailableDate.replace(/-/g, ""));
    const today = new Date();
    let formattedDate = SchedulingUtils.getFormattedDate(date, true);
    if (SchedulingUtils.areSameMonthDayYear(date, today)) {
      formattedDate = "Today";
    } else {
      // Turn today into tomorrow, that's the motto.
      today.setDate(today.getDate() + 1);
      if (SchedulingUtils.areSameMonthDayYear(date, today)) {
        formattedDate = "Tomorrow";
      }
    }
    header = (
      <span>
        First available <span style={{ fontWeight: "bold" }}>{currentDurationString}</span> time
        is&nbsp;
        <a style={styles.availableDate} onClick={() => onSetDate(date)} title="Search this day">
          {formattedDate}
        </a>
      </span>
    );
    subheader = dateIsInPast
      ? "Clockwise can't suggest times in the past"
      : "No good times before then";
  }

  return (
    <div style={styles.noSuggestions}>
      <div style={styles.noSuggestionsHeader}>{header}</div>
      <div style={styles.noSuggestionsSubheader}>{subheader}</div>
    </div>
  );
};

const LoadingSkeleton = () => (
  <div style={{ ...styles.mediumTile, ...styles.mediumTileMarginRight }}>
    <Skeleton variant="text" animation="wave" />
    <Skeleton variant="text" animation="wave" />
    <Skeleton variant="text" animation="wave" />
  </div>
);

export const TagTitle = ({
  tags,
  index,
  isSelected,
  numImpacts,
  styleOverrides = {},
  justIcon = false,
}: {
  tags: SchedulingAttendanceSummaryTag[];
  index: number;
  isSelected: boolean;
  numImpacts: number;
  styleOverrides?: CSSProperties;
  justIcon?: boolean;
}) => (
  <div
    style={Object.assign(
      {},
      isSelected ? styles.tileHeaderSelected : styles.tileHeader,
      numImpacts > 0 && !isSelected ? styles.darkPeach : {},
      styleOverrides,
    )}
  >
    {justIcon ? getFormattedTag(tags, index).icon : getFormattedTag(tags, index).full}
  </div>
);

const FormattedDate = ({ isSelected, startDate }: { isSelected: boolean; startDate: DateTime }) => (
  <div style={isSelected ? styles.timeSelected : styles.time}>
    {SchedulingUtils.getFormattedDateLuxon(startDate) +
      ", " +
      SchedulingUtils.getFormattedTimeLuxon(startDate)}
  </div>
);

const CALENDAR_FORMAT_OPTIONS: CalendarSpec = {
  sameDay: "'Today at' h:mm a",
  nextDay: "'Tomorrow at' h:mm a",
  nextWeek: "'Next' ccc',' MMM d 'at' h:mm a",
  lastDay: "'Yesterday' h:mm a",
  thisWeek: "'This' ccc',' MMM d 'at' h:mm a",
  lastWeek: "'Last' ccc',' MMM d 'at' h:mm a",
  sameElse: "ccc',' MMM d 'at' h:mm a",
};

const FormattedDateAndTime = ({
  isSelected,
  startDate,
  style,
  timeZone,
}: {
  isSelected: boolean;
  startDate: string;
  style: object;
  timeZone?: string;
}) => {
  const luxonOptions = timeZone ? { zone: timeZone } : { setZone: true };
  const luxonStartDateTime = DateTime.fromISO(startDate, luxonOptions).setLocale("en-US");

  const calendarTime = getRelativeFormat(luxonStartDateTime, CALENDAR_FORMAT_OPTIONS);

  return <div style={{ ...style, ...(isSelected ? { color: "white" } : {}) }}>{calendarTime}</div>;
};

const DATE_CALENDAR_FORMAT_OPTIONS: CalendarSpec = {
  sameDay: "'Today'",
  nextDay: "'Tomorrow'",
  nextWeek: "'Next' ccc',' MMM d",
  lastDay: "'Yesterday'",
  thisWeek: "'This' ccc',' MMM d",
  lastWeek: "'Last' ccc',' MMM d",
  sameElse: "ccc',' MMM d",
};
const ConfirmableFormattedDateAndTime = ({
  startDate,
  style,
  timeZone,
}: {
  startDate: string;
  style: object;
  timeZone?: string;
}) => {
  const luxonOptions = timeZone ? { zone: timeZone } : { setZone: true };
  const luxonStartDateTime = DateTime.fromISO(startDate, luxonOptions).setLocale("en-US");

  const calendarDate = getRelativeFormat(luxonStartDateTime, DATE_CALENDAR_FORMAT_OPTIONS);
  const calendarTime = luxonStartDateTime.toFormat("h:mm a");

  return (
    <div className="cw-flex cw-flex-col" style={style}>
      <div className="cw-text-sm">{calendarTime}</div>
      <div className="cw-text-slate-standard cw-text-[13px] cw-pt-1 cw-pb-0.5">{calendarDate}</div>
    </div>
  );
};

const FocusTimeAndIncons = ({
  isSelected,
  focusTimeMinutesCost,
  numImpacts,
}: {
  isSelected: boolean;
  focusTimeMinutesCost: number;
  numImpacts: number;
}) => (
  <div style={isSelected ? styles.qualifierSelected : styles.qualifier}>
    {focusTimeMinutesCost === 0 ? (
      <span>{numImpacts === 0 ? "No Focus Time lost" : ""}</span>
    ) : numImpacts === 0 ? (
      <span>
        <span style={isSelected ? styles.focusTime : styles.focusTimeDark}>
          {getFormattedFocusTime(focusTimeMinutesCost)}
        </span>
        &nbsp;Focus Time
      </span>
    ) : (
      <span
        style={Object.assign({}, isSelected ? styles.focusTime : styles.focusTimeDark, {
          paddingRight: 2,
        })}
      >
        {getFormattedFocusTime(focusTimeMinutesCost)}&nbsp;
        <Waves style={styles.smallFocusIcon} />
        &nbsp;
      </span>
    )}
    {numImpacts > 0 && (
      <span style={isSelected ? styles.impactsShortSelected : styles.impactsShort}>
        {focusTimeMinutesCost === 0 ? (
          numImpacts + " " + pluralize("Inconvenience", numImpacts)
        ) : (
          <span>
            {numImpacts}&nbsp;
            <ReportProblemOutlined style={styles.warningIcon} type="MaterialOutlined" />
          </span>
        )}
      </span>
    )}
  </div>
);

const FocusTimeAndInconsFull = ({
  focusTimeMinutesCost,
  numImpacts,
  style,
}: {
  focusTimeMinutesCost: number;
  numImpacts: number;
  style: object;
}) => (
  <div style={style}>
    {focusTimeMinutesCost === 0 ? (
      "No Focus Time lost"
    ) : (
      <span>
        <span style={{ fontWeight: "bold" }}>{getFormattedFocusTime(focusTimeMinutesCost)}</span>
        &nbsp;Focus Time
      </span>
    )}
    {numImpacts > 0 && (
      <span>
        &nbsp;/&nbsp;
        <span style={{ fontWeight: "bold" }}>{numImpacts}</span>
        &nbsp;{pluralize("inconvenience", numImpacts)}
      </span>
    )}
  </div>
);

//////////////////////////////////////////////////////////////////////////
// Medium Tile used in Google create event modal for inline suggestions //
//////////////////////////////////////////////////////////////////////////

export const SuggestedTimeTileMedium = ({
  slot,
  index,
  isLoading,
  isSelected,
  onSelect,
  onMouseEnter,
  onMouseLeave,
  _ref,
}: {
  slot: ISlot;
  index: number;
  isLoading: boolean;
  isSelected: boolean;
  onSelect: (index: number) => void;
  onMouseEnter: (index: number) => void;
  onMouseLeave: () => void;
  _ref?: ((instance: HTMLDivElement | null) => void) | undefined;
}) => {
  const startTime = slot.timeRange.startTime.dateOrDateTime,
    { tags, focusTimeMinutesCost } = slot.summary;
  const startDate = DateTime.fromISO(startTime, { setZone: true });
  const [numImpacts] = countImpacts(slot);
  const roomId = slot.roomImpacts?.[0]?.roomId;
  const style = isSelected ? styles.mediumTileSelected : styles.mediumTile;

  return (
    <div
      key={startTime + roomId}
      style={{ ...style, ...(isLoading ? { opacity: 0.4 } : { opacity: 1 }) }}
      onClick={onSelect.bind(null, index)}
      ref={_ref}
      onMouseEnter={() => onMouseEnter(index)}
      onMouseLeave={onMouseLeave}
    >
      <TagTitle tags={tags} index={index} isSelected={isSelected} numImpacts={numImpacts} />
      <FormattedDate isSelected={isSelected} startDate={startDate} />
      <FocusTimeAndIncons
        isSelected={isSelected}
        numImpacts={numImpacts}
        focusTimeMinutesCost={focusTimeMinutesCost}
      />
    </div>
  );
};

/////////////////////////////////////
// Large Tile used in the Sidebar //
/////////////////////////////////////

export type SuggestedTimeTimeLargePerson = GetAttendeesPainTextPerson;
export type SuggestedTimeTileLargeSlot = {
  timeRange: {
    startTime: {
      dateOrDateTime: string;
    };
  };
  summary: {
    tags: SchedulingAttendanceSummaryTag[];
    focusTimeMinutesCost: number;
  };
} & CountImpactSlot &
  GetAttendeesPainTextSchedulingSlot;
export const SuggestedTimeTileLarge = ({
  slot,
  index,
  isLoading,
  isSelected,
  onSelect,
  people,
  onMouseEnter,
  onMouseLeave,
  roomName,
  styleOverrides = {},
}: {
  slot: SuggestedTimeTileLargeSlot;
  index: number;
  isLoading: boolean;
  isSelected: boolean;
  onSelect: (slot: SuggestedTimeTileLargeSlot) => void;
  className?: string;
  people: SuggestedTimeTimeLargePerson[];
  onMouseEnter: (slot: SuggestedTimeTileLargeSlot) => void;
  onMouseLeave: () => void;
  roomName: string;
  styleOverrides?: CSSProperties;
}) => {
  const startDate = slot.timeRange.startTime.dateOrDateTime,
    { tags, focusTimeMinutesCost } = slot.summary;
  const [numImpacts, numImpactTypes] = countImpacts(slot);

  const [isExpanded, setIsExpanded] = React.useState(false);

  const pains = (slot && people && getAttendeesPainText(slot, people, false)) || [];
  // Always trying to avoid conditionality, but want to display the pains compact if
  // we have any uncrawled calenders, otherwise too much going on in the view.
  const hasUncrawledPain = pains.find((pain) => pain.pain === Inconveniences.Uncrawled);

  const tileStyle = isSelected ? styles.largeTileSelected : styles.largeTile;
  let painsStyle = isSelected ? styles.largeTilePainsSelected : styles.largeTilePains;

  // Sory for the web of styles in these components
  if ((numImpactTypes > 1 || hasUncrawledPain) && !isSelected && !isExpanded) {
    painsStyle = { ...painsStyle, backgroundColor: "white" };
  }

  return (
    <div
      key={startDate}
      style={
        isLoading && !isSelected
          ? { ...tileStyle, opacity: 0.4, ...styleOverrides }
          : { ...tileStyle, opacity: 1, ...styleOverrides }
      }
      onClick={() => {
        if (!isLoading) {
          onSelect(slot);
        }
      }}
      onMouseEnter={() => onMouseEnter(slot)}
      onMouseLeave={onMouseLeave}
      cw-id="suggested-time-tile-large"
    >
      <TagTitle
        tags={tags}
        index={index}
        isSelected={isSelected}
        numImpacts={numImpacts}
        styleOverrides={{ marginBottom: 0, fontSize: 13 }}
      />
      <FormattedDateAndTime
        isSelected={isSelected}
        startDate={startDate}
        style={styles.dateTimeLarge}
      />
      {roomName && (
        <div style={isSelected ? styles.largeTileRoomSelected : styles.largeTileRoom}>
          <MeetingRoom
            style={isSelected ? styles.meetingRoomIconSelected : styles.meetingRoomIcon}
          />
          <span>{roomName}</span>
        </div>
      )}
      <FocusTimeAndInconsFull
        numImpacts={numImpacts}
        focusTimeMinutesCost={focusTimeMinutesCost}
        style={
          isSelected ? { ...styles.focusTimeLargeTile, color: "white" } : styles.focusTimeLargeTile
        }
      />
      {pains?.length > 0 && (
        <div
          style={painsStyle}
          onClick={(e) => {
            if (numImpactTypes > 1 || hasUncrawledPain) {
              e.stopPropagation();
              setIsExpanded(!isExpanded);
            }
          }}
        >
          {(numImpactTypes > 1 || hasUncrawledPain) && !isExpanded ? (
            <AttendeesPainsCompact pains={pains} renderIsSelected={isSelected} />
          ) : (
            <AttendeesPains pains={pains} color={isSelected ? "white" : ""} />
          )}
          {(numImpactTypes > 1 || hasUncrawledPain) &&
            (isExpanded ? (
              <KeyboardArrowUp style={isSelected ? { color: "white" } : {}} />
            ) : (
              <KeyboardArrowDown style={isSelected ? { color: "white" } : {}} />
            ))}
        </div>
      )}
    </div>
  );
};

export const ConfirmableSuggestedTimeTileLarge = ({
  slot,
  index,
  isLoading,
  isSelected,
  onSelect,
  onConfirm,
  people,
  onMouseEnter,
  onMouseLeave,
  roomName,
  styleOverrides = {},
}: {
  slot: SuggestedTimeTileLargeSlot;
  index: number;
  isLoading: boolean;
  isSelected: boolean;
  onSelect: (slot: SuggestedTimeTileLargeSlot) => void;
  onConfirm: () => void;
  className?: string;
  people: SuggestedTimeTimeLargePerson[];
  onMouseEnter: (slot: SuggestedTimeTileLargeSlot) => void;
  onMouseLeave: () => void;
  roomName: string;
  styleOverrides?: CSSProperties;
}) => {
  const startDate = slot.timeRange.startTime.dateOrDateTime,
    { tags, focusTimeMinutesCost } = slot.summary;
  const [numImpacts, numImpactTypes] = countImpacts(slot);

  const [isExpanded, setIsExpanded] = React.useState(false);

  const pains = (slot && people && getAttendeesPainText(slot, people, false)) || [];
  // Always trying to avoid conditionality, but want to display the pains compact if
  // we have any uncrawled calenders, otherwise too much going on in the view.
  const hasUncrawledPain = pains.find((pain) => pain.pain === Inconveniences.Uncrawled);

  const tileStyle = styles.largeTile;
  let painsStyle = styles.largeTilePains;

  // Sory for the web of styles in these components
  if ((numImpactTypes > 1 || hasUncrawledPain) && !isExpanded) {
    painsStyle = { ...painsStyle, backgroundColor: "white" };
  }

  return (
    <div
      key={startDate}
      style={
        isLoading
          ? { ...tileStyle, opacity: 0.4, ...styleOverrides }
          : { ...tileStyle, opacity: 1, ...styleOverrides }
      }
      onClick={() => {
        if (!isLoading) {
          onSelect(slot);
        }
      }}
      onMouseEnter={() => onMouseEnter(slot)}
      onMouseLeave={onMouseLeave}
      cw-id="suggested-time-tile-large"
    >
      {!isSelected && (
        <>
          <TagTitle
            tags={tags}
            index={index}
            isSelected={isSelected}
            numImpacts={numImpacts}
            styleOverrides={{ marginBottom: 0, fontSize: 13 }}
          />
          <FormattedDateAndTime
            isSelected={isSelected}
            startDate={startDate}
            style={styles.dateTimeLarge}
          />
        </>
      )}
      {isSelected && (
        <motion.div
          className="cw-flex cw-flex-row cw-items-center cw-justify-between"
          initial={{ x: 300, opacity: 0.5 }}
          animate={{ x: 0, opacity: 1, transition: { type: "spring", bounce: 0, duration: 0.25 } }}
        >
          <div className="cw-flex cw-flex-col cw-w-1/2">
            <div style={styles.tileHeader}>PICK THIS TIME</div>
            <ConfirmableFormattedDateAndTime startDate={startDate} style={styles.dateTimeLarge} />
          </div>
          <div className="cw-flex cw-grow cw-justify-center cw-cursor-pointer">
            <Button
              className="cw-text-sm cw-font-bold cw-py-[10px] cw-px-[20px] cw-h-10 cw-text-white cw-normal-case cw-bg-green-darker"
              size="small"
              onClick={onConfirm}
            >
              Confirm
            </Button>
          </div>
        </motion.div>
      )}
      {roomName && (
        <div style={styles.largeTileRoom}>
          <MeetingRoom style={styles.meetingRoomIcon} />
          <span>{roomName}</span>
        </div>
      )}
      {!isSelected && (
        <FocusTimeAndInconsFull
          numImpacts={numImpacts}
          focusTimeMinutesCost={focusTimeMinutesCost}
          style={styles.focusTimeLargeTile}
        />
      )}
      {pains?.length > 0 && (
        <div
          style={painsStyle}
          onClick={(e) => {
            if (numImpactTypes > 1 || hasUncrawledPain) {
              e.stopPropagation();
              setIsExpanded(!isExpanded);
            }
          }}
        >
          {(numImpactTypes > 1 || hasUncrawledPain) && !isExpanded ? (
            <AttendeesPainsCompact pains={pains} renderIsSelected={false} />
          ) : (
            <AttendeesPains pains={pains} />
          )}
          {(numImpactTypes > 1 || hasUncrawledPain) &&
            (isExpanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />)}
        </div>
      )}
    </div>
  );
};

////////////////////////////////////////////////////
// Hover used beneath medium tile in Google modal //
////////////////////////////////////////////////////

export const SuggestedTimeTileHover = ({
  slot,
  index,
  people,
  rooms,
  needRoom,
}: {
  slot: ISlot;
  index: number;
  people: IPerson[];
  rooms: IRoom[];
  needRoom: boolean;
}) => {
  const startDate = slot.timeRange.startTime.dateOrDateTime,
    { tags, focusTimeMinutesCost } = slot.summary;
  const [numImpacts] = countImpacts(slot),
    roomId = slot.roomImpacts?.[0]?.roomId;

  const pains =
    (slot &&
      people &&
      getAttendeesPainText(
        { summary: slot.summary, attendanceImpacts: slot.attendanceImpacts },
        people,
        true,
      )) ||
    [];

  const maybeRoom = roomId && rooms.find((room) => room?.roomId === roomId);

  return (
    <div>
      <div style={styles.hoverStuffUppper}>
        <div style={Object.assign({}, styles.tileHeader, numImpacts > 0 ? styles.darkPeach : {})}>
          {getFormattedTag(tags, index).full}
        </div>
        <FormattedDateAndTime isSelected={false} startDate={startDate} style={styles.dateTime} />
        <FocusTimeAndInconsFull
          numImpacts={numImpacts}
          focusTimeMinutesCost={focusTimeMinutesCost}
          style={styles.hoverFocusTime}
        />
      </div>
      <div style={styles.impacts}>
        {needRoom && (
          <div>
            {maybeRoom ? (
              <div style={styles.meetingRoomRow}>
                <MeetingRoom style={styles.meetingRoomIcon} />
                <span>{maybeRoom.name}</span>
              </div>
            ) : (
              <div style={styles.meetingRoomRow}>
                <NoMeetingRoom style={styles.meetingRoomIcon} />
                <span>No rooms available</span>
              </div>
            )}
          </div>
        )}
        {numImpacts > 0 && <AttendeesPains pains={pains} />}
        {(numImpacts > 0 || needRoom) && <div style={styles.seperator} /> /* Horizontal Separator*/}
        <div
          style={Object.assign(
            {},
            styles.logoFeedback,
            numImpacts > 0 || needRoom ? { paddingTop: 7 } : {},
          )}
        >
          <LogoHorizontal {...styles.logo} />
          <a href={articleUrls.feedback} style={styles.feedback} target="_blank">
            Feedback
          </a>
        </div>
      </div>
    </div>
  );
};

/////////////////////////////////////////////////////////////////////////////////////
// Suggested Times Row, used to display a row of 0-3 medium tiles in the Google Modal
/////////////////////////////////////////////////////////////////////////////////////

const MAX_SUGGESTIONS_PER_PAGE = 3;

const SuggestedTimesRowCmpt = (props: IProps) => {
  const {
    suggestedTimesForEvent,
    isLoading,
    onSelect,
    dateIsInPast,
    selectedTile,
    parentContainer,
    needRoom,
    onHover,
    onSetDate,
    currentDurationString,
    hoveredSuggestionIndex,
    setHoveredSuggestionIndex,
    pageIndex,
    onCarouselLeftControlClick,
    onCarouselRightControlClick,
  } = props;

  const hoveredTileIndex = hoveredSuggestionIndex;
  const setHoveredTileIndex = setHoveredSuggestionIndex;

  const mouseEnterCallback = React.useCallback(
      (index) => {
        setHoveredTileIndex(index);
        onHover(suggestedTimesForEvent?.slots?.[index], true);
      },
      [suggestedTimesForEvent],
    ),
    mouseLeaveCallback = React.useCallback(() => {
      setHoveredTileIndex(-1);
      onHover(null, false);
    }, []);
  const slotRefs = React.useRef<Array<HTMLDivElement | null>>([]);
  const queryRecommendationDate = suggestedTimesForEvent?.queryRecommendation?.availableDate;

  // Do a .slice to be defensive about the slots. If webapp has the flag enabled but not crx, then slots
  // will be of length 9 but we will *not* render a carousel. This will cause visual jank. Thus, on the
  // crx side, we do this slice to guarantee the number of slots is in "sync" with whether we actually
  // will show the carousel.
  const slots = (suggestedTimesForEvent?.slots || []).slice(0, 9);

  React.useEffect(() => {
    if (slots.length !== 0) {
      slotRefs.current = slotRefs.current.slice(0, slots.length);
    }
  }, [suggestedTimesForEvent]);

  const numPages = Math.ceil(slots.length / MAX_SUGGESTIONS_PER_PAGE);
  const pages: JSX.Element[] = Array.from(Array(numPages).keys()).map((pageIndex) => {
    const elementsInCurrentPage = slots.slice(
      pageIndex * MAX_SUGGESTIONS_PER_PAGE,
      (pageIndex + 1) * MAX_SUGGESTIONS_PER_PAGE,
    );

    return (
      <div style={styles.carouselPageContainer} key={pageIndex}>
        {elementsInCurrentPage.map((slot: ISlot, intraPageIndex: number) => {
          const slotIndex = pageIndex * MAX_SUGGESTIONS_PER_PAGE + intraPageIndex;
          const notLastElement = intraPageIndex < MAX_SUGGESTIONS_PER_PAGE - 1;
          return (
            <div
              style={{
                ...styles.carouselPageItem,
                ...(notLastElement && styles.mediumTileMarginRight),
              }}
              key={slot.timeRange.startTime.dateOrDateTime + intraPageIndex}
            >
              <SuggestedTimeTileMedium
                slot={slot as ISlot}
                index={slotIndex}
                onMouseEnter={mouseEnterCallback}
                onMouseLeave={mouseLeaveCallback}
                isLoading={isLoading}
                _ref={(el: HTMLDivElement) => (slotRefs.current[slotIndex] = el)}
                isSelected={!isLoading && slotIndex === selectedTile}
                onSelect={() => {
                  if (!isLoading && selectedTile !== slotIndex) {
                    onSelect(slotIndex);
                  }
                }}
              />
            </div>
          );
        })}
      </div>
    );
  });

  const carouselControls = slots.length !== 0 && (
    <div style={styles.carouselControls}>
      <HoverableChevron
        direction="left"
        onClick={onCarouselLeftControlClick}
        disabled={pageIndex === 0}
      />
      <HoverableChevron
        direction="right"
        onClick={onCarouselRightControlClick}
        disabled={pageIndex === numPages - 1}
      />
    </div>
  );

  return (
    <div>
      <div style={{ width: "100%", position: "relative" }}>
        <div style={styles.rowContainer}>
          {(!!suggestedTimesForEvent?.slots?.length && (
            <Carousel
              pageIndex={pageIndex}
              onLeftControlClick={onCarouselLeftControlClick}
              onRightControlClick={onCarouselRightControlClick}
            >
              {pages}
            </Carousel>
          )) ||
            (isLoading && [1, 2, 3].map((i) => <LoadingSkeleton key={i} />)) || (
              <NoSuggestions
                dateIsInPast={dateIsInPast}
                firstAvailableDate={queryRecommendationDate}
                currentDurationString={currentDurationString}
                onSetDate={onSetDate}
              />
            )}
        </div>
        {carouselControls}
      </div>
      {suggestedTimesForEvent?.slots?.map((slot: ISlot, i: number) => (
        <Popper
          style={{ zIndex: 10001, maxWidth: 285 }}
          key={i}
          anchorEl={slotRefs.current[i]}
          open={!!slotRefs.current[i] && hoveredTileIndex === i}
          placement={
            // Attach to bottom-end if it is the last element in the current carousel page
            i % MAX_SUGGESTIONS_PER_PAGE === MAX_SUGGESTIONS_PER_PAGE - 1
              ? "bottom-end"
              : "bottom-start"
          }
          container={parentContainer}
        >
          <Paper
            elevation={3}
            style={{ zIndex: 10001 }}
            // onMouseEnter={() => setTimeout(() => mouseEnterCallback(i), 30)}
            // timeout ensures that this is called after the onLeave of the parent div
            // gets set. Small edge case for when mouse approaches from the bottom of the tile.
            onMouseEnter={() => mouseEnterCallback(i)}
            onMouseLeave={mouseEnterCallback}
          >
            <SuggestedTimeTileHover
              slot={slot}
              people={suggestedTimesForEvent?.people || []}
              index={i}
              rooms={suggestedTimesForEvent?.rooms || []}
              needRoom={needRoom}
            />
          </Paper>
        </Popper>
      ))}
    </div>
  );
};

//////////////////
// WITH STYLES
//////////////////

export const SuggestedTimesRow = withStyles(styles)(SuggestedTimesRowCmpt);
